(function (global, factory) {
  "use strict";

  var api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (global && global.document) {
    api.boot(global);
  }
})(typeof window !== "undefined" ? window : undefined, function () {
  "use strict";

  var STORAGE_KEY = "pk_bgm_preference_v1";
  var EVENTS = {
    context: "pk:bgm:context",
    gateway: "pk:bgm:gateway-start",
    store: "pk:bgm:store-enter",
  };

  function safeRead(storage) {
    try {
      var value = storage.getItem(STORAGE_KEY);
      return value === "enabled" || value === "muted" ? value : null;
    } catch (_error) {
      return null;
    }
  }

  function safeWrite(storage, value) {
    try {
      storage.setItem(STORAGE_KEY, value);
    } catch (_error) {
      return;
    }
  }

  function createController(root, environment) {
    var env = environment || {};
    var document = env.document;
    var storage = env.localStorage;
    var AudioConstructor = env.Audio;
    var requestFrame = env.requestAnimationFrame;
    var cancelFrame = env.cancelAnimationFrame;
    var now = env.now;
    var toggleButton = root.querySelector("[data-pk-bgm-toggle]");
    var targetVolumes = {
      game: Number(root.dataset.gameVolume) || 0.26,
      store: Number(root.dataset.storeVolume) || 0.16,
    };
    var configuredFadeMs = Number(root.dataset.fadeMs);
    var fadeMs = Number.isFinite(configuredFadeMs) ? Math.max(0, configuredFadeMs) : 2200;
    var audios = {
      game: new AudioConstructor(root.dataset.gameSrc),
      store: new AudioConstructor(root.dataset.storeSrc),
    };
    var state = {
      context: "store",
      activeTrack: null,
      preference: safeRead(storage),
      status: "idle",
      hidden: Boolean(document.hidden),
      suspended: false,
    };
    var frameId = null;
    var transitionToken = 0;
    var destroyed = false;
    var unlockListening = false;

    Object.keys(audios).forEach(function (key) {
      audios[key].preload = "none";
      audios[key].loop = true;
      audios[key].volume = 0;
    });

    function currentTrack() {
      return root.dataset.homeFilmMusic === "true" || state.context === "gateway" ? "game" : "store";
    }

    function updateControl() {
      var playing = state.status === "playing";
      root.dataset.state = state.status;
      toggleButton.setAttribute("aria-pressed", String(playing));
      toggleButton.setAttribute(
        "aria-label",
        playing ? "Turn ambient sound off" : "Turn ambient sound on",
      );
    }

    function cancelTransition() {
      transitionToken += 1;
      if (frameId !== null) cancelFrame(frameId);
      frameId = null;
    }

    function pauseAll(resetVolume) {
      Object.keys(audios).forEach(function (key) {
        audios[key].pause();
        if (resetVolume) audios[key].volume = 0;
      });
    }

    function handlePlayFailure(audio) {
      audio.pause();
      audio.volume = 0;
      state.status = "blocked";
      updateControl();
      addUnlockListeners();
    }

    async function playAudio(audio) {
      try {
        await audio.play();
        return true;
      } catch (_error) {
        handlePlayFailure(audio);
        return false;
      }
    }

    function completeTransition(track, otherTrack) {
      audios[track].volume = targetVolumes[track];
      audios[otherTrack].volume = 0;
      audios[otherTrack].pause();
      state.activeTrack = track;
      root.dataset.activeTrack = track;
      state.status = "playing";
      updateControl();
    }

    async function switchTrack(track) {
      if (destroyed || state.hidden || state.suspended || state.preference !== "enabled") return false;
      var otherTrack = track === "game" ? "store" : "game";
      var incoming = audios[track];
      var outgoing = audios[otherTrack];
      var incomingStart = incoming.volume;
      var outgoingStart = outgoing.volume;
      cancelTransition();
      var token = transitionToken;
      if (!(await playAudio(incoming))) return false;
      if (destroyed || state.hidden || state.suspended) { incoming.pause(); return false; }
      if (token !== transitionToken) return false;
      if (fadeMs === 0) {
        completeTransition(track, otherTrack);
        return true;
      }
      var startedAt = now();
      return new Promise(function (resolve) {
        function step(timestamp) {
          if (destroyed || token !== transitionToken) {
            resolve(false);
            return;
          }
          var progress = Math.min(1, Math.max(0, (timestamp - startedAt) / fadeMs));
          incoming.volume = incomingStart + ((targetVolumes[track] - incomingStart) * progress);
          outgoing.volume = outgoingStart * (1 - progress);
          if (progress >= 1) {
            frameId = null;
            completeTransition(track, otherTrack);
            resolve(true);
            return;
          }
          frameId = requestFrame(step);
        }
        frameId = requestFrame(step);
      });
    }

    function removeUnlockListeners() {
      if (!unlockListening) return;
      unlockListening = false;
      document.removeEventListener("click", unlockFromGesture, true);
      document.removeEventListener("keydown", unlockFromGesture, true);
    }

    function unlockFromGesture(event) {
      if (event && (root.contains(event.target) || (event.target.closest && event.target.closest('#PKFilmHero')))) return;
      removeUnlockListeners();
      if (state.preference !== "enabled" || state.hidden || state.suspended) return;
      switchTrack(currentTrack()).catch(function () {});
    }

    function addUnlockListeners() {
      if (unlockListening || state.preference !== "enabled") return;
      unlockListening = true;
      document.addEventListener("click", unlockFromGesture, true);
      document.addEventListener("keydown", unlockFromGesture, true);
    }

    function setContext(context) {
      if (context !== "gateway" && context !== "store") return;
      state.context = context;
      root.dataset.context = context;
    }

    async function enableFor(track) {
      state.preference = "enabled";
      safeWrite(storage, "enabled");
      removeUnlockListeners();
      return switchTrack(track);
    }

    function startGateway() {
      setContext("gateway");
      return enableFor("game");
    }

    function enterStore() {
      setContext("store");
      return enableFor(currentTrack());
    }

    function suspend() {
      state.suspended = true;
      cancelTransition();
      removeUnlockListeners();
      pauseAll(true);
      state.status = "paused";
      updateControl();
    }

    function resume() {
      if (!state.suspended) return Promise.resolve(false);
      state.suspended = false;
      return switchTrack(currentTrack());
    }

    async function toggle() {
      if (state.suspended) {
        state.suspended = false;
        return enableFor(currentTrack());
      }
      if (state.preference === "enabled" && state.status === "playing") {
        cancelTransition();
        pauseAll(true);
        state.preference = "muted";
        state.status = "muted";
        safeWrite(storage, "muted");
        removeUnlockListeners();
        updateControl();
        return false;
      }
      return enableFor(currentTrack());
    }

    async function onVisibilityChange() {
      state.hidden = Boolean(document.hidden);
      if (state.hidden) {
        cancelTransition();
        pauseAll(false);
        if (state.preference === "enabled") state.status = "paused";
        updateControl();
        return;
      }
      if (state.preference === "enabled") {
        await switchTrack(currentTrack());
      }
    }

    function onContext(event) {
      setContext(event && event.detail && event.detail.context);
      if (state.preference === "enabled" && !state.suspended) switchTrack(currentTrack()).catch(function () {});
    }

    function onGatewayStart() {
      startGateway().catch(function () {});
    }

    function onStoreEnter() {
      enterStore().catch(function () {});
    }

    function onToggle() {
      toggle().catch(function () {});
    }

    function getState() {
      return {
        context: state.context,
        activeTrack: state.activeTrack,
        preference: state.preference,
        status: state.status,
        hidden: state.hidden,
        suspended: state.suspended,
      };
    }

    function getAudio() {
      return audios;
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelTransition();
      removeUnlockListeners();
      pauseAll(true);
      toggleButton.removeEventListener("click", onToggle);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener(EVENTS.context, onContext);
      document.removeEventListener(EVENTS.gateway, onGatewayStart);
      document.removeEventListener(EVENTS.store, onStoreEnter);
    }

    toggleButton.addEventListener("click", onToggle);
    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener(EVENTS.context, onContext);
    document.addEventListener(EVENTS.gateway, onGatewayStart);
    document.addEventListener(EVENTS.store, onStoreEnter);
    if (state.preference === "enabled") addUnlockListeners();
    else if (state.preference === "muted") state.status = "muted";
    root.dataset.context = state.context;
    updateControl();

    return {
      setContext: setContext,
      startGateway: startGateway,
      enterStore: enterStore,
      toggle: toggle,
      suspend: suspend,
      resume: resume,
      getState: getState,
      getAudio: getAudio,
      destroy: destroy,
    };
  }

  function boot(global) {
    function initialize() {
      if (global.PKBgm) return;
      var root = global.document.querySelector("[data-pk-bgm]");
      if (!root) return;
      var storage;
      try {
        storage = global.localStorage;
      } catch (_error) {
        storage = { getItem: function () { return null; }, setItem: function () {} };
      }
      try {
        global.PKBgm = createController(root, {
          document: global.document,
          localStorage: storage,
          Audio: global.Audio,
          requestAnimationFrame: typeof global.requestAnimationFrame === "function"
            ? function (callback) { return global.requestAnimationFrame(callback); }
            : function (callback) { return global.setTimeout(function () { callback(Date.now()); }, 16); },
          cancelAnimationFrame: typeof global.cancelAnimationFrame === "function"
            ? function (id) { global.cancelAnimationFrame(id); }
            : function (id) { global.clearTimeout(id); },
          now: function () {
            return global.performance && typeof global.performance.now === "function"
              ? global.performance.now()
              : Date.now();
          },
        });
        root.dataset.initialized = "true";
        global.document.dispatchEvent(new global.CustomEvent("pk:bgm:ready"));
        if (global.PKBgm.getState().preference !== "muted") global.PKBgm.enterStore().catch(function () {});
      } catch (error) {
        root.dataset.initialized = "false";
        root.dataset.initializationError = error && error.name ? error.name : "Error";
        root.dataset.state = "blocked";
      }
    }
    if (global.document.readyState === "loading") {
      global.document.addEventListener("DOMContentLoaded", initialize, { once: true });
    } else {
      initialize();
    }
  }

  return { createController: createController, boot: boot };
});
